import { useCallback, useEffect, useRef, useState } from 'react';

export type CatalogAutocompleteKey = 'category' | 'tags' | 'material' | 'color' | 'supplier';

export function useCatalogAutocompleteOpen(isCatalogModalOpen: boolean) {
  const [openAutocompleteKey, setOpenAutocompleteKey] = useState<CatalogAutocompleteKey | null>(
    null,
  );
  const suppressOpenRef = useRef(false);
  const wasCatalogModalOpenRef = useRef(false);

  useEffect(() => {
    if (wasCatalogModalOpenRef.current && !isCatalogModalOpen) {
      suppressOpenRef.current = true;
      setOpenAutocompleteKey(null);
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        active.blur();
      }
    }
    wasCatalogModalOpenRef.current = isCatalogModalOpen;
  }, [isCatalogModalOpen]);

  const getAutocompleteOpenProps = useCallback(
    (key: CatalogAutocompleteKey, catalogOnOpen: () => void) => ({
      open: !isCatalogModalOpen && openAutocompleteKey === key,
      onOpen: () => {
        if (suppressOpenRef.current) {
          suppressOpenRef.current = false;
          return;
        }
        setOpenAutocompleteKey(key);
        catalogOnOpen();
      },
      onClose: () => {
        suppressOpenRef.current = false;
        setOpenAutocompleteKey(null);
      },
    }),
    [isCatalogModalOpen, openAutocompleteKey],
  );

  const closeAutocompleteAndOpenModal = useCallback((openModal: () => void) => {
    setOpenAutocompleteKey(null);
    openModal();
  }, []);

  return { getAutocompleteOpenProps, closeAutocompleteAndOpenModal };
}
